import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { font } from "../constants/font";
import Typography from "./Typography";
import { STATIC_PATH } from "../constants/config";
import useDebounce from "../helper/hooks/useDebouncs";
import CustomTooltipComponent from "./CustomTooltip";

const DropDown = styled.div`
  width: ${(props) => props.width || "auto"};
  height: ${(props) => props.height || "auto"};
  background: ${(props) => !props.disableBg && props.theme.polarColor};
  border: ${(props) =>
    props.required && !props.hasValue
      ? "1px solid #ff4444"
      : props.border || `1px solid ${props.theme.primaryBorderColor}`};
  border-radius:${(props) => props.radius || "6px"};
  margin: ${(props) => props.margin || "8px 0px 12px"};
  position: ${(props) => props.position || "relative"};
  ${(props) =>
    props.isOpen &&
    css`
      border: ${(props) =>
        props.required && !props.hasValue
          ? "1px solid #ff4444"
          : props.borderActive || `1px solid ${props.theme.secondaryBorderColor}`};
    `}
`;

const OptionWrapper = styled.ul`
  list-style: none;
  margin-top: 5px;
  padding: 0px;
  background-color: ${(props) => props.theme.polarColor};
  border-radius: ${(props) => props.borderRadius};
  box-shadow: 0px 0px 4px ${(props) => props.theme.boxShadowColor};
  width: ${(props) => props.optionWidth || "262px"};
  height: ${(props) => props.optionHeight || "auto"};
  position: absolute;
  z-index: 9;
  top: ${(props) => props.optionTop ? props.optionTop : `${props.top + 47}px`};
  left: ${(props) => props.optionLeft || "unset"};
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y !important;
  @media screen and (max-width: 767px) {
    max-height: ${(props) => props.optionHeight || "200px"};
    // padding-bottom: 40px;
    padding-top: 4px;
    scroll-padding-bottom: 20px;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y !important;
    will-change: scroll-position;
  }
`;

const Option = styled.div`
  color: ${(props) => props.theme.propertyColor};
  width: 100%;
  display: inline-block;
  width: -webkit-fill-available;
  padding: 12px 10px;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  font: ${font.normalBase};
  position: relative;
  opacity: ${(props) => props.isDisabled ? 0.5 : 1};
  touch-action: auto;
  &:after {
      content: "";
      position: absolute;
      right: 15px;
      top: auto;
      width: 4px;
      height: 12px;
      border: solid
        ${(props) => (props.isDisabled ? "grey" : props.theme.sideBarproperty)};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      ${props => !props.showTick && css`
        display: none;
      `}
    }
  &:hover {
    background: ${(props) => props.theme.mercuryColor};
  }
  @media screen and (max-width: 767px) {
    &:last-child {
      margin-bottom: 8px;
    }
  }
  img {
    margin: 2px 0px -5px 5px;
    &.upgradeIcon{
      float: right;
      position: relative;
      top: 50%;
      transform: translateY(27%);
    }
  }
`;

const Selected = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${(props) => props.theme.propertyColor};
  padding: ${(props) => props.padding || "10px"};
  font: ${font.normalBase};
  position: relative;
  cursor: pointer;
`;

const Backdrop = styled.div`
  width: ${(props) => props.backdropWidth || "100%"};
  width: ${(props) => props.backdropHeight || "100%"};
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${(props) => props.zIndex};
`;

const Label = styled.label`
  display: block;
  font: ${props => props.labelFont || font.normalBase_16};
  color: ${props => props.labelColor};
  margin: ${(props) => props.labelMargin || "0px 0px 10px"};
  top: -25px;
`;

const RequiredStar = styled.span`
  color: ${(props) => props.theme.warningColor};
  margin-left: 4px;
  font: ${font.normalBase_16};
`;
const Subtext = styled.span`
display: inline-block;
font: ${(props) => (props.languageSubText ? `${font.normalBase}` : `${font.normalMicro_16}`)};
letter-spacing: 0px;
color: ${props => props.labelSubtextColor};
width: ${(props) => (props.languageSubText ? "auto" : "100%")};
margin: ${(props) => (props.languageSubText ? "0px 10px;" : "4px 0px 0px;")};
`;

const LabelDropdown = ({
  options,
  width,
  height,
  right,
  border,
  labelEnable,
  labelText,
  borderActive,
  margin,
  optionWidth,
  fontStyle,
  defaultValue,
  onChange,
  enableInputBox,
  position,
  enableAutoAlign,
  labelFont,
  labelColor,
  requiredStar = true,
  required,
  labelMargin,
  isBottom,
  isScroll,
  borderRadius,
  optionHeight,
  radius,
  backdropWidth,
  backdropHeight,
  languageSubText,
  padding,
  optionTop,
  optionLeft,
  optionBottom,
  disableBg,
  enablePlaceholder
}) => {
  const optionRef = useRef(null);
  const openRef = useRef(null);
  const [top, setTop] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownBottom, setDropdownBottom] = useState("");
  const [placeHolder, setPlaceHolder] = useState(false);
  const [selectedOption, setSelectedOption] = useState({
    label: options[0].label,
    value: options[0].value,
    subtext: options[0].subtext,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    if (defaultValue) {
      const selectedData = options.filter(a => a.value === defaultValue);
      selectedData.length && setSelectedOption({
        ...selectedData[0]
      });
    }

  }, [defaultValue, options]);

  useEffect(() => {
    if (optionRef.current) {
      const clientRect = optionRef.current.getBoundingClientRect();
      if (isBottom) {
        setDropdownBottom(optionBottom ? "unset" : "29px");
        setTop("auto");
      } else {
        setTop(clientRect.top - 12);
      }
    }
    if (isOpen && !isBottom && isScroll) {
      openRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isOpen, isBottom, isScroll, optionBottom]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onOptionClick = useCallback((option) => {
    onChange(option);
    if (!option.isUpgrade) {
      setSelectedOption(option);
    }
    setPlaceHolder(false);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onInputChange = useCallback((e) => {
    const { value } = e.target;
    onOptionClick({
      label: value,
      value: value !== "" && Number(value),
    });
  })

  const debouncedSetSearchTerm = useCallback(useDebounce((term) => {
    // Filter options based on the first letter matching words only
    const filtered = options.filter((option) =>
      option.value !== "" && option.label.toLowerCase().startsWith(term.toLowerCase())
    );
    setFilteredOptions(filtered);

    // Scroll to the first matching option
    if (filtered.length > 0) {
      const matchedOption = filtered[0];
      const matchedOptionElement = document.getElementById(`option-${matchedOption.value}`);
      if (matchedOptionElement) {
        matchedOptionElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, 300), [options]);

  useEffect(() => {
    debouncedSetSearchTerm(searchTerm);
  }, [searchTerm, debouncedSetSearchTerm]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      // ESC → close dropdown
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        optionRef.current?.focus();
        return;
      }

      // SEARCH (letters & numbers only)
      const char = e.key.toLowerCase();
      if (char.length === 1 && /[a-z0-9]/.test(char)) {
        setSearchTerm(char);

        const matchedOption = options.find(
          (option) =>
            option.value !== "" &&
            option.label.toLowerCase().startsWith(char)
        );

        if (matchedOption) {
          document
            .getElementById(`option-${matchedOption.value}`)
            ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, options]);

  useEffect(() => {
    if (enablePlaceholder) {
      setPlaceHolder(true);
    }
  }, [enablePlaceholder]);

  // Reset searchTerm and filtered options when dropdown is closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions(options.filter(option => option.value !== ""));
    }
  }, [isOpen, options]);

  return (
    <>
      {labelEnable && (
        <Label labelFont={labelFont} labelColor={labelColor} labelMargin={labelMargin}>
          {labelText}
          {requiredStar && <RequiredStar>*</RequiredStar>}
        </Label>
      )}
      <DropDown
        position={position}
        className="drop-down"
        onClick={() => setIsOpen(!isOpen)}
        width={width}
        height={height}
        isOpen={isOpen}
        margin={margin}
        border={border}
        radius={radius}
        borderActive={borderActive}
        labelEnable={labelEnable}
        ref={optionRef}
        isScroll={isScroll}
        disableBg={disableBg}
        required={required}
        hasValue={selectedOption.value !== ""}
      >
        <Selected className="selected-option" padding={padding}>
          {!enableInputBox ? (
            <Typography
              innerContent={placeHolder ? "Select..." : selectedOption.label}
              {...fontStyle}
              font={font.normalBase}
              cursor="pointer"
            />
          ) : (
            <input
              className="dropdown-input"
              type="number"
              value={selectedOption.value}
              onChange={onInputChange}
              onBlur={() =>
                setTimeout(() => {
                  setIsOpen(false);
                })
              }
            />
          )}
          <img
            src={`${STATIC_PATH}downArrow.svg`}
            alt="close"
            style={isOpen ? { transform: "rotate(180deg)" } : {}}
          />
        </Selected>
        {isOpen && (
          <>
            <Backdrop backdropWidth={backdropWidth} backdropHeight={backdropHeight} onClick={() => setIsOpen(false)} zIndex={1} />
            <OptionWrapper
              className="dropdown-options-wrapper"
              optionWidth={optionWidth}
              borderRadius={borderRadius}
              optionHeight={optionHeight}
              top={enableAutoAlign ? top : "35px"}
              style={{ bottom: dropdownBottom }}
              ref={openRef}
              optionTop={optionTop}
              optionLeft={optionLeft}
            >
              {filteredOptions.map((option, index) => (
                option.tooltip ? (
                  <CustomTooltipComponent
                    key={`${option.label + index}`}
                    tooltipId={`option-tooltip-${option.value}`}
                    dataTooltip={option.tooltip}
                    tooltipPosition={"top"}
                    width="240px"
                    height="58px"
                    visible={true}
                    font={font.normal_13}
                    nonFormattedMessage={true}
                  >
                    <Option
                      id={`option-${option.value}`}
                      data-tooltip-id={`option-tooltip-${option.value}`}
                      className="dropdown-option-item"
                      onClick={() => {
                        if (!option.isDisabled) {
                          onOptionClick(option);
                        }
                      }}
                      right={right}
                      isDisabled={option.isDisabled}
                      showTick={!option.isUpgrade && option.label === selectedOption.label && option.value === selectedOption.value}
                    >
                      <Typography
                        font={!option.isUpgrade && option.label === selectedOption.label ? font.mediumBase_21 : font.normalBase}
                        innerContent={option.label}
                        {...fontStyle}
                        subtext={option.subtext}
                        cursor={option.isDisabled ? "not-allowed" : "pointer"}
                      />

                      {option.isUpgrade &&
                        <img src={`${STATIC_PATH}${option.icon || `vm-bg-star.svg`}`} alt="upgrade-icon" className="upgradeIcon" />
                      }
                      {option.subtext &&
                        (languageSubText ? (
                          <Subtext languageSubText={languageSubText}>({option.subtext})</Subtext>
                        ) : (
                          <Subtext>{option.subtext}</Subtext>
                        ))}
                    </Option>
                  </CustomTooltipComponent>
                ) : (
                  <Option
                    id={`option-${option.value}`}
                    className="dropdown-option-item"
                    key={`${option.label + index}`}
                    onClick={() => {
                      if (!option.isDisabled) {
                        onOptionClick(option);
                      }
                    }}
                    right={right}
                    isDisabled={option.isDisabled}
                    showTick={!option.isUpgrade && option.label === selectedOption.label && option.value === selectedOption.value}
                  >
                    <Typography
                      font={!option.isUpgrade && option.label === selectedOption.label ? font.mediumBase_21 : font.normalBase}
                      innerContent={option.label}
                      {...fontStyle}
                      subtext={option.subtext}
                      cursor={option.isDisabled ? "not-allowed" : "pointer"}
                    />

                    {option.isUpgrade &&
                      <img src={`${STATIC_PATH}${option.icon || `vm-bg-star.svg`}`} alt="upgrade-icon" className="upgradeIcon" />
                    }
                    {option.subtext &&
                      (languageSubText ? (
                        <Subtext languageSubText={languageSubText}>({option.subtext})</Subtext>
                      ) : (
                        <Subtext>{option.subtext}</Subtext>
                      ))}
                  </Option>
                )
              ))}
            </OptionWrapper>
          </>
        )}
      </DropDown>
    </>
  );
};

LabelDropdown.defaultProps = {
  onChange: () => { },
  enableInputBox: false,
  enableAutoAlign: false,
};

LabelDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      subtext: PropTypes.string,
      id: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  width: PropTypes.string,
  height: PropTypes.string,
  margin: PropTypes.string,
  border: PropTypes.string,
  borderActive: PropTypes.string,
  optionWidth: PropTypes.string,
  right: PropTypes.string,
  fontStyle: PropTypes.object,
  defaultValue: PropTypes.number,
  onChange: PropTypes.func,
  enableInputBox: PropTypes.bool,
  labelEnable: PropTypes.bool,
  enableAutoAlign: PropTypes.bool,
  labelText: PropTypes.string,
  position: PropTypes.string,
  labelColor: PropTypes.string,
  labelFont: PropTypes.string,
  requiredStar: PropTypes.bool,
  required: PropTypes.bool,
  labelMargin: PropTypes.string,
  isBottom: PropTypes.bool,
  isScroll: PropTypes.bool,
  borderRadius: PropTypes.string,
  optionHeight: PropTypes.string,
  radius: PropTypes.string,
  backdropWidth: PropTypes.string,
  backdropHeight: PropTypes.string,
  languageSubText: PropTypes.bool,
  padding: PropTypes.string,
  optionTop: PropTypes.string,
  optionLeft: PropTypes.string,
  optionBottom: PropTypes.string,
  disableBg: PropTypes.bool,
  enablePlaceholder: PropTypes.bool
};

export default LabelDropdown;
